/**
 * UI module for Vuex store
 */

// Sorting controls are only offered once a list is long enough for order to
// really matter. Below this the list is shown as-is (chronologically).
export const MIN_TRACKS_FOR_SORT_OPTIONS = 21;

// Year labels share the same threshold: short lists don't need year headers.
export const MIN_TRACKS_FOR_YEAR_LABELS = 21;

export default {
  namespaced: true,
  
  state: () => ({
    sortOrder: 'chronologically',
    isShowingSearchBar: false,
    searchQuery: '',
    searchResults: [],
    isUserTyping: false,
    darkMode: false,
    snackbar: {
      isVisible: false,
      text: '',
      timeout: 3000,
    },
    isHighQualityAudioMode: false,
    isCreatePlaylistMode: false,
    isPollMode: false,
    isPollResultsMode: false,
    yearsCollapsed: [],
    isVideoMode: false,
  }),
  
  getters: {
    sortOrder: state => state.sortOrder,
    isShowingSearchBar: state => state.isShowingSearchBar,
    searchQuery: state => state.searchQuery,
    searchResults: state => state.searchResults,
    isUserTyping: state => state.isUserTyping,
    darkMode: state => state.darkMode,
    snackbar: state => state.snackbar,
    isHighQualityAudioMode: state => state.isHighQualityAudioMode,
    isCreatePlaylistMode: state => state.isCreatePlaylistMode,
    isPollMode: state => state.isPollMode,
    isPollResultsMode: state => state.isPollResultsMode,
    yearsCollapsed: state => state.yearsCollapsed,
    isVideoMode: state => state.isVideoMode,

    shouldShowYearLabels: (state, getters, rootState, rootGetters) => {
      // Never show year labels for the default playlist
      if (!state.isVideoMode && rootGetters['playlist/isDefaultPlaylist']) {
        return false;
      }

      const recordings = state.isVideoMode
        ? rootGetters['video/videos']
        : rootGetters['playlist/recordings'];

      return (
        rootGetters['auth/isLoggedIn'] &&
        state.sortOrder === 'chronologically' &&
        recordings.length >= MIN_TRACKS_FOR_YEAR_LABELS &&
        state.searchQuery === ''
      );
    },

    /**
     * Whether the sort toggle (chronological <-> alphabetical) is offered.
     * Shown to EVERYONE — logged in or out, on any playlist — as soon as the
     * current list has at least MIN_TRACKS_FOR_SORT_OPTIONS tracks. Hidden on
     * poll results, which are ranked by votes and not sortable.
     */
    shouldShowSortOptions: (state, getters, rootState, rootGetters) => {
      if (state.isPollResultsMode) {
        return false;
      }

      const items = state.isVideoMode
        ? rootGetters['video/videos']
        : rootGetters['playlist/recordings'];

      return items.length >= MIN_TRACKS_FOR_SORT_OPTIONS;
    },
  },
  
  mutations: {
    SET_SORT_ORDER(state, order) {
      state.sortOrder = order;
    },
    SET_IS_SHOWING_SEARCH_BAR(state, isShowingSearchBar) {
      state.isShowingSearchBar = isShowingSearchBar;
    },
    SET_SEARCH_QUERY(state, query) {
      state.searchQuery = query;
    },
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
    SET_IS_USER_TYPING(state, isUserTyping) {
      state.isUserTyping = isUserTyping;
    },
    SET_DARK_MODE(state, isDarkMode) {
      state.darkMode = isDarkMode;
    },
    SET_SNACKBAR(state, { isVisible, text, timeout }) {
      state.snackbar = {
        isVisible: isVisible !== undefined ? isVisible : state.snackbar.isVisible,
        text: text !== undefined ? text : state.snackbar.text,
        timeout: timeout !== undefined ? timeout : state.snackbar.timeout,
      };
    },
    SET_HIGH_QUALITY_AUDIO_MODE(state, isHighQualityAudioMode) {
      state.isHighQualityAudioMode = isHighQualityAudioMode;
    },
    SET_CREATE_PLAYLIST_MODE(state, isCreatePlaylistMode) {
      state.isCreatePlaylistMode = isCreatePlaylistMode;
    },
    SET_POLL_MODE(state, isPollMode) {
      state.isPollMode = isPollMode;
    },
    SET_POLL_RESULTS_MODE(state, isPollResultsMode) {
      state.isPollResultsMode = isPollResultsMode;
    },
    TOGGLE_YEAR_COLLAPSE(state, year) {
      const index = state.yearsCollapsed.indexOf(year);
      if (index === -1) {
        state.yearsCollapsed.push(year);
      } else {
        state.yearsCollapsed.splice(index, 1);
      }
    },
    CLEAR_YEAR_COLLAPSES(state) {
      state.yearsCollapsed = [];
    },
    SET_VIDEO_MODE(state, isVideoMode) {
      state.isVideoMode = isVideoMode;
    },
  },
  
  actions: {
    setSortOrder({ commit, dispatch }, order) {
      commit('SET_SORT_ORDER', order);
      dispatch('showSnackbar', `Sorted ${order}`);
    },
    
    toggleSearchBar({ commit, state }) {
      if (state.isShowingSearchBar) {
        commit('SET_IS_SHOWING_SEARCH_BAR', false);
        commit('SET_SEARCH_QUERY', '');
        commit('SET_SEARCH_RESULTS', []);
        commit('SET_IS_USER_TYPING', false);
      } else {
        commit('SET_IS_SHOWING_SEARCH_BAR', true);

        // Scroll to top so search bar is visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    updateSearchQuery({ commit, rootGetters, state }, query) {
      commit('SET_SEARCH_QUERY', query);

      // Search in videos or recordings depending on current mode
      const searchResults = [];
      const items = state.isVideoMode
        ? rootGetters['video/videos']
        : rootGetters['playlist/recordings'];
      const regex = new RegExp(query.toLowerCase().trim());

      items.forEach(item => {
        if (regex.test(item.name.toLowerCase())) {
          searchResults.push(item.id);
        }
      });

      commit('SET_SEARCH_RESULTS', searchResults);
    },
    
    setUserTyping({ commit }, isTyping) {
      commit('SET_IS_USER_TYPING', isTyping);
    },
    
    toggleDarkMode({ commit, state }) {
      commit('SET_DARK_MODE', !state.darkMode);
    },
    
    showSnackbar({ commit }, payload) {
      const text = typeof payload === 'string' ? payload : payload.text;
      const timeout = (typeof payload === 'object' && payload.timeout) ? payload.timeout : 3000;
      commit('SET_SNACKBAR', { isVisible: true, text, timeout });
    },
    
    hideSnackbar({ commit }) {
      commit('SET_SNACKBAR', { isVisible: false });
    },
    
    setHighQualityAudioMode({ commit }, isHighQuality) {
      commit('SET_HIGH_QUALITY_AUDIO_MODE', isHighQuality);
    },
    
    setCreatePlaylistMode({ commit }, isCreatePlaylistMode) {
      commit('SET_CREATE_PLAYLIST_MODE', isCreatePlaylistMode);
      // playlist and poll modes are mutually exclusive
      if (isCreatePlaylistMode) {
        commit('SET_POLL_MODE', false);
      }
    },

    setPollMode({ commit }, isPollMode) {
      commit('SET_POLL_MODE', isPollMode);
      // playlist and poll modes are mutually exclusive
      if (isPollMode) {
        commit('SET_CREATE_PLAYLIST_MODE', false);
      }
    },

    setPollResultsMode({ commit }, isPollResultsMode) {
      commit('SET_POLL_RESULTS_MODE', isPollResultsMode);
    },
    
    toggleYearCollapse({ commit }, year) {
      commit('TOGGLE_YEAR_COLLAPSE', year);
    },
    
    clearYearCollapses({ commit }) {
      commit('CLEAR_YEAR_COLLAPSES');
    },

    async toggleVideoMode({ commit, state, dispatch, rootGetters }) {
      const newMode = !state.isVideoMode;

      // Fade out BEFORE changing mode to prevent component destruction during fade
      // When switching to video mode, fade out audio first
      if (newMode && rootGetters['player/isPlaying']) {
        await dispatch('player/pauseTrackWithFade', 200, { root: true });
      }

      // When switching to audio mode, fade out video first (before VideoPlayer is destroyed)
      // Use longer duration for video since YouTube iframe API has more latency
      if (!newMode && rootGetters['video/isPlaying']) {
        await dispatch('video/pauseVideoWithFade', 300, { root: true });
      }

      // Now change the mode (this may destroy/create components)
      commit('SET_VIDEO_MODE', newMode);

      // Turn off playlist mode when switching to video mode (no custom playlist support for videos)
      if (newMode && state.isCreatePlaylistMode) {
        commit('SET_CREATE_PLAYLIST_MODE', false);
      }

      // Polls are audio-only — turn off poll mode when switching to video mode
      if (newMode && state.isPollMode) {
        commit('SET_POLL_MODE', false);
      }

      // Show snackbar notification
      const modeText = newMode ? 'Video Mode' : 'Audio Mode';
      dispatch('showSnackbar', `Switched to ${modeText}`);

      // Clear search when switching modes
      if (state.isShowingSearchBar) {
        dispatch('toggleSearchBar');
      }
    },
  }
};